// Save, delete, notify, manual check-in and encounter mapping, kept out of the container.

import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { useAppDispatch } from 'common/hooks/redux'
import type { EHRAppointmentFormType } from 'features/Home/schemas/infoNewPatientSchema'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { useOverwriteAppointmentEncounterMutation } from 'features/Profile/features/TransactionsMapping/transactionsMapping.api'
import { SCHEDULE_TAGS, scheduleApi } from 'features/Schedule/api/schedule.api'

import {
  buildCreateDto,
  buildUpdateDto,
  type CreateExtras,
  type UpdateOptions,
} from './buildAppointmentDto'
import { appointmentDay } from './manualCheckIn'

const NOTIFY_LOCKOUT_MS = 60 * 1000

const lockoutKey = (appointmentId?: number): string | null =>
  appointmentId ? `patient-${appointmentId}-disabledUntil` : null

type ScheduleActions = {
  createAppointment: (...args: never[]) => Promise<{ id?: number } | undefined>
  updateAppointment: (...args: never[]) => Promise<unknown>
  notifyPatient: (...args: never[]) => Promise<unknown>
  deleteAppointment: (...args: never[]) => Promise<unknown>
  checkInPatient: (...args: never[]) => Promise<unknown>
}

type Args = {
  siteId?: string
  editedPatientData?: IPatientSchedule
  actions: ScheduleActions
  saveAppointmentIssues: (appointmentId?: number) => Promise<unknown>
  saveCreatedAppointmentIssues: (appointmentId?: number) => Promise<unknown>
  saveAppointmentComplaints: (appointmentId?: number | null) => Promise<boolean>
  // Only an OE opening saves complaints and the problem list.
  withClinical: boolean
}

export const useAppointmentActions = ({
  siteId,
  editedPatientData,
  actions,
  saveAppointmentIssues,
  saveCreatedAppointmentIssues,
  saveAppointmentComplaints,
  withClinical,
}: Args) => {
  const dispatch = useAppDispatch()
  const { setNotification } = useNotification()
  const [overwriteEncounter, { isLoading: isResolvingEncounter }] =
    useOverwriteAppointmentEncounterMutation()

  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | undefined>()
  const [encounterError, setEncounterError] = useState<string | undefined>()
  const [mappedEncounterId, setMappedEncounterId] = useState<number | null>(null)
  const [notifyDisabledUntil, setNotifyDisabledUntil] = useState(0)

  // The dialog stays mounted between appointments, so every per-appointment value is re-read here.
  useEffect(() => {
    setMappedEncounterId(null)
    setEncounterError(undefined)

    const key = lockoutKey(editedPatientData?.id)
    setNotifyDisabledUntil(key ? Number(localStorage.getItem(key) ?? 0) : 0)
  }, [editedPatientData?.id])

  // Without a timer the button would stay disabled past the lockout until something else re-renders.
  useEffect(() => {
    const remaining = notifyDisabledUntil - Date.now()
    if (remaining <= 0) return

    const timer = setTimeout(() => setNotifyDisabledUntil(0), remaining)
    return () => clearTimeout(timer)
  }, [notifyDisabledUntil])

  const create = useCallback(
    async (values: EHRAppointmentFormType, extras: CreateExtras) => {
      const create = actions.createAppointment as (
        data: unknown,
        date: string,
      ) => Promise<{ id?: number }>
      const created = await create(
        buildCreateDto(values, extras),
        dayjs(values.date).format('YYYY-MM-DD'),
      )
      if (!withClinical) return created
      try {
        await saveCreatedAppointmentIssues(created?.id)
      } catch {
        // The appointment exists; saving again would duplicate it, so only the issues are reported.
        setNotification({
          title: 'Appointment saved',
          description: 'The problem list could not be saved. Open the appointment and try again.',
          type: ALERT_CONSTANTS.ERROR,
        })
      }

      return created
    },
    [actions, saveCreatedAppointmentIssues, setNotification, withClinical],
  )

  // Complaints go first: a Checked Out set by this same save would freeze them.
  const update = useCallback(
    async (values: EHRAppointmentFormType, options: UpdateOptions = {}): Promise<boolean> => {
      if (withClinical && !(await saveAppointmentComplaints(editedPatientData?.id))) return false

      const update = actions.updateAppointment as (
        data: unknown,
        date: string,
        id?: number,
      ) => Promise<unknown>
      await update(
        buildUpdateDto(values, editedPatientData?.patient?.id, options),
        dayjs(values.date).format('YYYY-MM-DD'),
        editedPatientData?.id,
      )
      if (withClinical) await saveAppointmentIssues(editedPatientData?.id)
      return true
    },
    [actions, editedPatientData, saveAppointmentComplaints, saveAppointmentIssues, withClinical],
  )

  // A failed delete must not look like a successful one.
  const remove = useCallback(
    async (sendSms: boolean): Promise<boolean> => {
      setIsDeleting(true)
      setDeleteError(undefined)
      try {
        const remove = actions.deleteAppointment as (
          date?: string,
          id?: number,
          sendSms?: number,
        ) => Promise<unknown>
        await remove(editedPatientData?.date, editedPatientData?.id, sendSms ? 1 : 0)
        setNotification({
          title: 'Delete Success',
          description: 'Appointment deleted',
          type: ALERT_CONSTANTS.SUCCESS,
        })
        return true
      } catch (error) {
        // useScheduleActions already raised the notification; keep the reason for support.
        setDeleteError(error instanceof Error ? error.message : 'Could not delete the appointment')
        return false
      } finally {
        setIsDeleting(false)
      }
    },
    [actions, editedPatientData, setNotification],
  )

  const notify = useCallback(async () => {
    const send = actions.notifyPatient as (id?: number) => Promise<unknown>
    await send(editedPatientData?.id)

    const key = lockoutKey(editedPatientData?.id)
    if (!key) return

    const until = Date.now() + NOTIFY_LOCKOUT_MS
    localStorage.setItem(key, String(until))
    setNotifyDisabledUntil(until)
  }, [actions, editedPatientData])

  // Legacy manual check-in: the access code on the appointment's own site day.
  const checkIn = useCallback(async (): Promise<boolean> => {
    const day = appointmentDay(editedPatientData)
    if (!editedPatientData?.id || !editedPatientData.access_code || !day) return false

    const send = actions.checkInPatient as (
      id: number,
      accessCode: string,
      date: string,
    ) => Promise<unknown>
    try {
      await send(editedPatientData.id, editedPatientData.access_code, day)
      return true
    } catch {
      // useScheduleActions already raised the notification.
      return false
    }
  }, [actions, editedPatientData])

  const mapEncounter = useCallback(
    async ({ encounterId, encounterUuid }: { encounterId: number; encounterUuid: string }) => {
      if (!editedPatientData?.id || !siteId) {
        const message = 'This appointment cannot be mapped yet. Reopen it and try again.'
        setEncounterError(message)
        setNotification({ title: 'Error', description: message, type: ALERT_CONSTANTS.ERROR })
        return false
      }

      try {
        const response = await overwriteEncounter({
          appointment_id: editedPatientData.id,
          encounter_id: encounterId,
          encounter_uuid: encounterUuid,
          siteId,
        }).unwrap()

        if (response && response.status === false) {
          const message = response.message || 'Failed to map encounter'
          setEncounterError(message)
          setNotification({ title: 'Error', description: message, type: ALERT_CONSTANTS.ERROR })
          return false
        }

        setMappedEncounterId(encounterId)
        setEncounterError(undefined)
        dispatch(
          scheduleApi.util.invalidateTags([SCHEDULE_TAGS.SCHEDULE, SCHEDULE_TAGS.SCHEDULE_WEEK]),
        )
        setNotification({
          title: 'Success',
          description: response?.message || 'Encounter mapped successfully',
          type: ALERT_CONSTANTS.SUCCESS,
        })
        return true
      } catch (error: unknown) {
        const message =
          (error as { data?: string[]; message?: string })?.data?.[0] ??
          (error as Error)?.message ??
          'Failed to map encounter'
        setEncounterError(message)
        setNotification({ title: 'Error', description: message, type: ALERT_CONSTANTS.ERROR })
        return false
      }
    },
    [dispatch, editedPatientData, overwriteEncounter, setNotification, siteId],
  )

  return {
    create,
    update,
    remove,
    notify,
    checkIn,
    mapEncounter,
    isDeleting,
    deleteError,
    isResolvingEncounter,
    encounterError,
    mappedEncounterId,
    isNotifyLocked: Date.now() < notifyDisabledUntil,
  }
}
